import { cn } from '@/lib/utils';

/**
 * The institute console's accent, in one place.
 *
 * **Blue is the console's colour.** The chrome already says so — the sidebar's
 * mark is `blue-500 → indigo-600 → sky-600` and the top bar's avatar is
 * `blue-600 → indigo-600` — so every screen inside that frame is painted from
 * the same blue rather than each room picking a hue of its own. Screens that
 * once carried an indigo, a violet and a teal between them read as three
 * products; one accent reads as one.
 *
 * The shared fields, selects, panels and alerts on these pages paint themselves
 * from `--accent`, `--line` and `--text-*`. Redeclaring those variables on a
 * page's root re-tints every one of them without a single component being
 * forked (CLAUDE.md §3) — which is also why this is one exported constant and
 * not a copy per feature: three copies of a palette is three chances for the
 * screen you save on to look like a different product from the one you came
 * from.
 *
 * Arbitrary *property* utilities rather than a `style` object, so this stays
 * Tailwind-only and still switches with the theme (§7).
 *
 * One thing it cannot reach: `Select` and `DatePicker` render their open panel
 * into `document.body`, outside this subtree, so a dropdown while it is open
 * still draws in the product's own palette.
 */
export const CONSOLE_PALETTE = cn(
  '[--accent:#2563eb] [--accent-2:#1d4ed8] [--accent-soft:#eff6ff] [--accent-line:#bfdbfe]',
  '[--line:#e2e8f0] [--line-strong:#cbd5e1]',
  '[--text:#0f172a] [--text-2:#475569] [--text-3:#94a3b8]',
  '[--wash-a:rgba(37,99,235,0.10)] [--wash-b:rgba(99,102,241,0.08)]',
  '[&_.lbl]:text-slate-700 dark:[&_.lbl]:text-slate-300 [&_label]:text-slate-700 dark:[&_label]:text-slate-300',

  'dark:[--accent:#60a5fa] dark:[--accent-2:#93c5fd] dark:[--accent-soft:#172554]',
  'dark:[--accent-line:#1d4ed8]',
  'dark:[--surface:#111c2e] dark:[--surface-2:#16233a] dark:[--surface-3:#1e2b45]',
  'dark:[--line:#1e293b] dark:[--line-strong:#334155]',
  'dark:[--text:#e2e8f0] dark:[--text-2:#94a3b8] dark:[--text-3:#64748b]',
  'dark:[--wash-a:rgba(96,165,250,0.12)] dark:[--wash-b:rgba(129,140,248,0.08)]',

  '[&_.lbl]:text-slate-700 dark:[&_.lbl]:text-slate-200 [&_.lbl]:text-sm [&_.lbl]:font-bold',
  '[&_label]:text-slate-700 dark:[&_label]:text-slate-200 [&_label]:text-sm [&_label]:font-bold',
  '[&_legend]:text-slate-700 dark:[&_legend]:text-slate-200 [&_legend]:text-sm [&_legend]:font-bold',
);

/**
 * The softer corners and header styling these screens give the shared `FormSection` panels.
 *
 * Only the header/title gets a subtle tinted background, while the card body below
 * is clean solid white/dark with no gradient wash.
 */
export const CONSOLE_PANEL_SKIN = cn(
  '[&_section.card]:rounded-3xl [&_section.card]:bg-white dark:[&_section.card]:bg-[#111c2e] [&_section.card]:[background-image:none] [&_section.card]:border-slate-200/90 dark:[&_section.card]:border-slate-800',
  '[&_section.card>header]:rounded-t-3xl [&_section.card>header]:bg-slate-100 dark:[&_section.card>header]:bg-[#0b1322] [&_section.card>header]:border-b [&_section.card>header]:border-slate-200 dark:[&_section.card>header]:border-slate-800',
  '[&_.lbl]:text-slate-700 dark:[&_.lbl]:text-slate-200 [&_.lbl]:text-sm [&_.lbl]:font-bold',
  '[&_label]:text-slate-700 dark:[&_label]:text-slate-200 [&_label]:text-sm [&_label]:font-bold',
  '[&_legend]:text-slate-700 dark:[&_legend]:text-slate-200 [&_legend]:text-sm [&_legend]:font-bold',
);
